import type { Result } from '../types/index'

import { tryCatch } from './tryCatch'

/**
 * Optional context used to attribute an upstream response failure.
 * The URL is recorded on the typed error for observability.
 */
export interface ParseJsonResponseOptions {
  /** Optional upstream URL to include in the parse error context. */
  url?: string
}

/**
 * Describes an upstream response that could not be consumed as JSON.
 *
 * The error records the HTTP `status`, response `contentType`, optional
 * upstream `url`, a bounded `bodySnippet`, and whether the body was detected
 * as HTML via `isHtml`. It is returned as the `error` from
 * {@link parseJsonResponse} and used as the `cause` of the error thrown by
 * {@link fetchJsonResponse} when the body is non-JSON, HTML, or unreadable.
 */
export class UpstreamResponseError extends Error {
  public readonly status: number
  public readonly contentType: string | null
  public readonly url?: string
  public readonly bodySnippet: string
  public readonly isHtml: boolean

  constructor({
    status,
    contentType,
    url,
    bodySnippet,
    isHtml,
    message,
    cause
  }: {
    status: number
    contentType: string | null
    url?: string
    bodySnippet: string
    isHtml: boolean
    message?: string
    cause?: unknown
  }) {
    const prefix = isHtml
      ? `Upstream returned HTML (HTTP ${status}):`
      : `Failed to parse response (HTTP ${status}):`
    super(`${message ?? `${prefix} ${bodySnippet}`}${url ? ` (${url})` : ''}`, { cause })
    this.name = 'UpstreamResponseError'
    this.status = status
    this.contentType = contentType
    this.url = url
    this.bodySnippet = bodySnippet
    this.isHtml = isHtml
  }
}

/**
 * Safely parse a `Response` as JSON. When parsing fails, reads the body as text to detect HTML
 * error pages (e.g. Cloudflare 502/429) and extracts the `<title>` or a body snippet so that
 * callers get an actionable {@link UpstreamResponseError} instead of
 * `SyntaxError: Unexpected token '<'`. Body-read failures are returned as the
 * same typed error with the underlying read exception preserved as its cause.
 *
 * @param response - Upstream response to parse.
 * @param options - Optional upstream-attribution context.
 */
export async function parseJsonResponse<T>(
  response: Response,
  options?: ParseJsonResponseOptions
): Promise<Result<T, UpstreamResponseError>> {
  let text: string
  try {
    text = await response.text()
  } catch (cause) {
    return {
      data: null,
      error: new UpstreamResponseError({
        status: response.status,
        contentType: response.headers.get('content-type'),
        url: options?.url,
        bodySnippet: '',
        isHtml: false,
        message: `Failed to read response body (HTTP ${response.status})`,
        cause
      })
    }
  }

  try {
    return { data: JSON.parse(text) as T, error: null }
  } catch {
    const isHtml = text.trimStart().startsWith('<')
    const title = isHtml ? text.match(/<title[^>]*>(.*?)<\/title>/i)?.[1]?.trim() : undefined
    const bodySnippet = title || text.slice(0, 200)
    return {
      data: null,
      error: new UpstreamResponseError({
        status: response.status,
        contentType: response.headers.get('content-type'),
        url: options?.url,
        bodySnippet,
        isHtml
      })
    }
  }
}

/** @internal */
export async function fetchJsonResponse<T>(url: string, requestInit?: RequestInit): Promise<T> {
  const { data: response, error: fetchError } = await tryCatch(fetch(url, requestInit))

  if (fetchError || !response?.ok) {
    throw Error(`HTTP ${response?.status ?? 'network error'}: ${url}`, { cause: fetchError })
  }

  const { data, error: parseError } = await parseJsonResponse<T>(response, { url })
  if (parseError) {
    throw Error(`Failed to parse JSON: ${url}`, { cause: parseError })
  }

  return data
}
